/**
 * @file resolveHeldAttachmentPushAlerts.ts
 * @description Turns this reader's held pushes into the alerts the attachments
 * panel shows. Consumer: `AttachmentHeldPushAlerts`, which owns only the
 * listener and the dismissal.
 *
 * The two reasons read differently and the difference is the point.
 * `noWriteAccess` is terminal — the agent cannot push and will not until someone
 * grants write access — so it may say the sending is stopped.
 * `formVersionNotDraft` is not stopped and must never say so: the version is
 * frozen, the agent keeps the file, and the push resumes on its own when the
 * version reopens. The draft gate runs before the server's idempotency lookup,
 * so a recovery commit for a version that already landed can be refused while
 * the save is safely in the chain; an alert claiming finality would be wrong for
 * the whole length of a freeze, which can be days.
 *
 * Grouped by reason, never one alert per record: three files held for one reason
 * are one alert naming three files. A `fileUid` the artifact's map does not
 * carry is named as unnamed rather than dropped — the reservation lane can
 * refuse a push for a file id that was never created.
 *
 * Names come from `resolveCurrentAttachmentName` alone. The raw-email alias is
 * deliberately not applied: `raw-email-*.json` is minted by email ingestion, not
 * pushed by an agent, so threading the alias context here would buy nothing.
 *
 * `nowMillis` is injected so the age is a pure function of its inputs. An
 * `attemptedAt` that is not a `Timestamp` yields no age rather than a coerced
 * one; no record is ever hidden for being old.
 */

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Timestamp } from 'firebase/firestore';

import type { HeldAttachmentPush, HeldAttachmentPushReason, TimeType } from '#types';
import type { ArtifactAttachments } from '#types/System.types';
import { resolveCurrentAttachmentName } from './resolveAttachmentDisplayName';

dayjs.extend(relativeTime);

/** Stopped outranks waiting, so the reader meets the terminal one first. */
const REASON_ORDER: readonly HeldAttachmentPushReason[] = ['noWriteAccess', 'formVersionNotDraft'];

const DISMISS_NOTE =
    'Dismissing hides this notice. It does not send the file, and a further refusal shows the notice again.';

export type HeldAttachmentPushAlertsRequest = {
    /** Already narrowed to the signed-in user by `useHeldAttachmentPushes`. */
    pushes: readonly HeldAttachmentPush[];
    /** The artifact's attachment map, which supplies the file names. */
    attachments: ArtifactAttachments;
    /** Read once per render, so the age is a pure function of the inputs. */
    nowMillis: number;
};

export type HeldAttachmentPushAlert = {
    reason: HeldAttachmentPushReason;
    /** The antd `Alert` type. `error` only for the reason that is genuinely stopped. */
    severity: 'error' | 'info';
    title: string;
    /** What happened, what it did not cost, and what ends it. */
    body: string;
    /** The most recent refusal's age, or null when no `attemptedAt` is readable. */
    age: string | null;
    /** What dismissing does and does not do. */
    dismissNote: string;
    /** Every file this alert covers; dismissing deletes exactly these records. */
    fileUids: readonly string[];
    ariaLabel: string;
    dismissAriaLabel: string;
};

const attemptedMillis = (attemptedAt: TimeType | undefined): number | null =>
    attemptedAt instanceof Timestamp ? attemptedAt.toMillis() : null;

/** `a`, `a and b`, `a, b and c`. */
const joinLabels = (labels: readonly string[]): string =>
    labels.length <= 1
        ? labels[0] ?? ''
        : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;

/**
 * Names the files an alert covers. Ids the map cannot name collapse into one
 * counted phrase, because repeating the same nameless label reads as a defect.
 */
const describeFiles = (
    pushes: readonly HeldAttachmentPush[],
    attachments: ArtifactAttachments,
): string => {
    const named = pushes
        .map((push) => resolveCurrentAttachmentName(attachments[push.fileUid]))
        .filter((name): name is string => typeof name === 'string')
        .sort((left, right) => left.localeCompare(right));

    const unnamed = pushes.length - named.length;
    if (unnamed === 0) {
        return joinLabels(named);
    }

    return joinLabels([
        ...named,
        unnamed === 1
            ? 'a file this document does not name'
            : `${unnamed} files this document does not name`,
    ]);
};

const alertTitle = (reason: HeldAttachmentPushReason): string =>
    reason === 'noWriteAccess'
        ? 'Syncretic File Sync stopped sending your changes'
        : 'Syncretic File Sync is waiting for this form version to reopen';

const alertBody = (reason: HeldAttachmentPushReason, files: string): string =>
    reason === 'noWriteAccess'
        ? `Syncretic File Sync could not send ${files}, because you cannot change this document. Nothing reached the document, and nothing was deleted from your computer. Sending starts again once someone grants you write access.`
        : `This form version is frozen, so Syncretic File Sync has not sent ${files} yet. Nothing was deleted from your computer, and Syncretic File Sync sends what it is holding as soon as the version reopens.`;

export const resolveHeldAttachmentPushAlerts = ({
    pushes,
    attachments,
    nowMillis,
}: HeldAttachmentPushAlertsRequest): readonly HeldAttachmentPushAlert[] =>
    REASON_ORDER.flatMap((reason) => {
        const group = pushes.filter((push) => push.reason === reason);
        if (group.length === 0) {
            return [];
        }

        const attempts = group
            .map((push) => attemptedMillis(push.attemptedAt))
            .filter((millis): millis is number => millis !== null);

        const title = alertTitle(reason);
        const alert: HeldAttachmentPushAlert = {
            reason,
            severity: reason === 'noWriteAccess' ? 'error' : 'info',
            title,
            body: alertBody(reason, describeFiles(group, attachments)),
            age: attempts.length === 0 ? null : dayjs(Math.max(...attempts)).from(dayjs(nowMillis)),
            dismissNote: DISMISS_NOTE,
            fileUids: group.map((push) => push.fileUid),
            ariaLabel: title,
            dismissAriaLabel: `Dismiss the notice that ${title}`,
        };

        return [alert];
    });
